import { fetchDocumentHtml } from '@/utils/document-utils';

export interface Size {
  width: number;
  height: number;
}

/** Common Storyline player default — used when native size cannot be resolved. */
export const DEFAULT_GAME_CONTENT_SIZE: Size = {
  width: 1009,
  height: 662,
};

const MIN_GAME_DIMENSION = 300;
const MAX_GAME_DIMENSION = 2400;

export const getScaleToFit = (container: Size, content: Size): number => {
  if (
    container.width <= 0 ||
    container.height <= 0 ||
    content.width <= 0 ||
    content.height <= 0
  ) {
    return 1;
  }

  return Math.min(
    container.width / content.width,
    container.height / content.height,
  );
};

export const isValidContentSize = (
  size: Size | null | undefined,
): size is Size => !!size && size.width > 0 && size.height > 0;

export const measureIframeContentSize = (
  iframe: HTMLIFrameElement,
): Size | null => {
  try {
    const doc = iframe.contentDocument ?? iframe.contentWindow?.document;

    if (!doc) {
      return null;
    }

    const root = doc.documentElement;
    const body = doc.body;
    const width = Math.max(
      root.scrollWidth,
      root.clientWidth,
      body.scrollWidth,
    );
    const height = Math.max(
      root.scrollHeight,
      root.clientHeight,
      body.scrollHeight,
    );

    if (width <= 0 || height <= 0) {
      return null;
    }

    return { width, height };
  } catch {
    return null;
  }
};

const isReasonableGameSize = ({ width, height }: Size): boolean =>
  width >= MIN_GAME_DIMENSION &&
  width <= MAX_GAME_DIMENSION &&
  height >= MIN_GAME_DIMENSION &&
  height <= MAX_GAME_DIMENSION;

const addCandidate = (candidates: Size[], width: number, height: number) => {
  const size = { width, height };

  if (isReasonableGameSize(size)) {
    candidates.push(size);
  }
};

/** Parse likely native player dimensions from a game index HTML document. */
export const parseGameHtmlDimensions = (html: string): Size | null => {
  const candidates: Size[] = [];

  const viewportMatch = /content=["'][^"']*width=(\d+)[^"']*height=(\d+)/i.exec(
    html,
  );

  if (viewportMatch) {
    addCandidate(
      candidates,
      Number.parseInt(viewportMatch[1] ?? '', 10),
      Number.parseInt(viewportMatch[2] ?? '', 10),
    );
  }

  const jsonPairMatch = /"width"\s*:\s*(\d+)\s*,\s*"height"\s*:\s*(\d+)/.exec(
    html,
  );

  if (jsonPairMatch) {
    addCandidate(
      candidates,
      Number.parseInt(jsonPairMatch[1] ?? '', 10),
      Number.parseInt(jsonPairMatch[2] ?? '', 10),
    );
  }

  const uniWidthMatch = /uniWidth\s*[=:]\s*(\d+)/i.exec(html);
  const uniHeightMatch = /uniHeight\s*[=:]\s*(\d+)/i.exec(html);

  if (uniWidthMatch && uniHeightMatch) {
    addCandidate(
      candidates,
      Number.parseInt(uniWidthMatch[1] ?? '', 10),
      Number.parseInt(uniHeightMatch[1] ?? '', 10),
    );
  }

  const styleMatches = html.matchAll(/width:\s*(\d+)px;\s*height:\s*(\d+)px/gi);

  for (const match of styleMatches) {
    addCandidate(
      candidates,
      Number.parseInt(match[1] ?? '', 10),
      Number.parseInt(match[2] ?? '', 10),
    );
  }

  if (candidates.length === 0) {
    return null;
  }

  return (
    candidates.sort(
      (left, right) => right.width * right.height - left.width * left.height,
    )[0] ?? null
  );
};

const resolveUrl = (candidate: string, baseUrl: string): string => {
  try {
    return new URL(candidate, baseUrl).href;
  } catch {
    return candidate;
  }
};

export const isSameOriginUrl = (url: string): boolean => {
  try {
    return new URL(url).origin === window.location.origin;
  } catch {
    return false;
  }
};

const fetchGameContentSizeFromUrl = async (
  url: string,
  depth = 0,
): Promise<Size | null> => {
  if (depth > 2 || !isSameOriginUrl(url)) {
    return null;
  }

  try {
    const html = await fetchDocumentHtml(url);
    const parsed = parseGameHtmlDimensions(html);

    if (parsed) {
      return parsed;
    }

    const trimmed = html.trim();

    if (
      trimmed.startsWith('https://') &&
      !trimmed.includes('<') &&
      trimmed.length < 500
    ) {
      return await fetchGameContentSizeFromUrl(trimmed, depth + 1);
    }

    const iframeSrc = /<iframe[^>]+src=["']([^"']+)["']/i.exec(html)?.[1];

    if (iframeSrc) {
      return await fetchGameContentSizeFromUrl(
        resolveUrl(iframeSrc, url),
        depth + 1,
      );
    }
  } catch {
    return null;
  }

  return null;
};

export const resolveGameContentSize = async (
  url: string,
  initialSize?: Size,
): Promise<Size> => {
  if (isValidContentSize(initialSize)) {
    return initialSize;
  }

  const fetchedSize = await fetchGameContentSizeFromUrl(url);

  if (isValidContentSize(fetchedSize)) {
    return fetchedSize;
  }

  return DEFAULT_GAME_CONTENT_SIZE;
};
